import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { Controller, useForm, useWatch, type Control, type FieldPath } from 'react-hook-form';
import MaskInput, { Masks, type Mask } from 'react-native-mask-input';

import { useFormFooterInset } from '@/components/mobile-bottom-nav';
import { PAYMENTS_TERMS_VERSION } from '@/components/legal/payments-terms-section';
import { PaymentAccountStatusScreen } from '@/components/payments/payment-account-status-screen';
import { ControlledOptionRows, FieldBlock } from '@/components/services/wizard/field';
import { ThemedText } from '@/components/themed-text';
import { Checkbox } from '@/components/ui/checkbox';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/ui/loading-button';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonForm } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { BANKS, searchBanks, type Bank } from '@/constants/banks';
import {
  ACTIVATION_BLOCKED_FALLBACK,
  ACTIVATION_PLAN_REQUIRED_FALLBACK,
  ACTIVATION_STEP_ADDRESS,
  ACTIVATION_STEP_BANK,
  ACTIVATION_STEP_PEP,
  ACTIVATION_STEP_REVIEW,
  ACTIVATION_STEP_YOU,
} from '@/content/payments';
import { formatFeeLine, formatSettlementLine } from '@/content/payments/shared';
import { useActivatePaymentAccount, usePaymentAccount } from '@/hooks/use-payment-account';
import { usePaymentPricing } from '@/hooks/use-payment-pricing';
import {
  clearPaymentAccountDraft,
  loadPaymentAccountDraft,
  savePaymentAccountDraft,
} from '@/lib/payment-account-draft';
import { fetchAddressByCep } from '@/services/cepService';
import { TEST_TOOLS_ENABLED } from '@/utils/test-tools';
import { showToast } from '@/store/toastStore';
import {
  PAYMENT_ACCOUNT_ACCOUNT_TYPE,
  PAYMENT_ACCOUNT_BUSINESS_TYPE,
  PaymentAccount,
  PaymentAccountActivationDraft,
  PaymentAccountPrefill,
} from '@/types/payment-account';
import { getApiErrorCode, getApiErrorMessage } from '@/utils/api-error';
import { cn } from '@/utils/cn';

import {
  ACTIVATION_DEFAULT_VALUES,
  ACTIVATION_STEP_FIELDS,
  activationSchema,
  applyDraft,
  DEV_ACTIVATION_TEST_VALUES,
  mapServerFieldToFormPath,
  stepForField,
  toActivationPayload,
  toDraft,
  type ActivationForm,
} from './activation-model';
import { usePlanFeature } from '@/hooks/use-plan-feature';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';

const STEP_LABELS = ['Você', 'Endereço', 'PEP', 'Banco', 'Revisão'];
const CEP_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/];
const AGENCY_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
const ACCOUNT_MASK = Array.from({ length: 20 }, () => /\d/);
const ACCOUNT_DIGIT_MASK = [/[0-9Xx]/];
const webInputReset = Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined;
const [termsBefore, termsAfter] = ACTIVATION_STEP_REVIEW.termsLabel.split(
  ACTIVATION_STEP_REVIEW.termsLinkLabel,
);

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/profile' as never);
}

function applyPrefill(defaults: ActivationForm, prefill?: PaymentAccountPrefill): ActivationForm {
  if (!prefill) return defaults;
  return {
    ...defaults,
    legalName: prefill.legalName ?? defaults.legalName,
    bank: {
      ...defaults.bank,
      bankName: prefill.bank?.bankName ?? defaults.bank.bankName,
      agency: prefill.bank?.agency ?? defaults.bank.agency,
      account: prefill.bank?.account ?? defaults.bank.account,
      accountDigit: prefill.bank?.accountDigit ?? defaults.bank.accountDigit,
    },
  };
}

/**
 * Assistente de ativação do recebimento automático (PLANO, seção 6.3,
 * T4.3). Cinco etapas do nosso formulário (calm-ui, seção 5: `zodResolver`
 * único, passos montados e alternados por `display`, portão em cada
 * `trigger()`); ao concluir, `POST /payment-account` **uma única vez** e a
 * conta criada vira o ponto de encaixe do componente do Stripe (T4.4).
 *
 * Se a conta já existe (envio único já aconteceu, em qualquer status),
 * esta tela mostra os estados reais de pós-ativação — "Em análise" /
 * "Pronto!" / "Pendência" e os menos comuns `RESTRICTED`/`DISABLED` — via
 * `PaymentAccountStatusScreen` (T4.5). É também para onde apontam as portas
 * de entrada da T4.5: card do Perfil quando já ativou, item "Recebimentos"
 * da sidebar/menu mobile (placeholder até a tela Recebimentos da T6.1
 * existir) e a faixa de Cobranças.
 */
export function PaymentActivationScreen() {
  const { data: statusData, isLoading: isStatusLoading } = usePaymentAccount();
  const { data: pricing } = usePaymentPricing();
  const autoPaymentPlan = usePlanFeature('AUTO_PAYMENT');
  const [draft, setDraft] = useState<PaymentAccountActivationDraft | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    void loadPaymentAccountDraft().then((loaded) => {
      if (mounted) setDraft(loaded);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const isLoading = isStatusLoading || draft === undefined;

  if (isLoading) {
    return (
      <PageLayout title="Ativar recebimento automático" onBack={goBack}>
        <SkeletonForm fields={5} />
      </PageLayout>
    );
  }

  // Conta já existe (envio único já aconteceu, em qualquer status): mostra o
  // estado real (T4.5), antes de checar `prefill` — quem só pode ver a conta
  // (equipe, não dono) tem `prefill: null` mas ainda assim vê o status; as
  // ações que só quem gerencia pode tomar (`useCanManagePaymentAccount`,
  // T7.2) somem sozinhas dentro de `PaymentAccountStatusScreen`.
  if (statusData?.account) {
    return <PaymentAccountStatusScreen account={statusData.account} />;
  }

  // Plano gratuito: antes do formulário inteiro, não depois de preenchido.
  if (autoPaymentPlan.locked) {
    return (
      <PageLayout title="Ativar recebimento automático" onBack={goBack}>
        <ErrorState
          icon="card-outline"
          title="Recebimento automático é dos planos pagos"
          message="Com um plano, o cliente paga por Pix ou cartão no link e a cobrança dá baixa sozinha. No gratuito, você segue recebendo por fora e marcando como pago."
          onRetry={() =>
            void navigateToSubscriptionPlans({ push: (path) => router.push(path as never) })
          }
          retryLabel="Ver planos"
        />
      </PageLayout>
    );
  }

  // `prefill: null` (contrato real, revisão da T1.4): quem só pode ver o
  // estado da conta, não geri-la, e ainda não existe conta pra ver — mesma
  // tela de "só o dono ativa" que o 403 do POST mostra.
  if (!statusData?.prefill) {
    return (
      <PageLayout title="Ativar recebimento automático" onBack={goBack}>
        <ErrorState
          title={ACTIVATION_BLOCKED_FALLBACK.title}
          message={ACTIVATION_BLOCKED_FALLBACK.body}
        />
      </PageLayout>
    );
  }

  return (
    <ActivationWizard
      prefill={statusData.prefill}
      draft={draft}
      feeLine={pricing ? formatFeeLine(pricing) : null}
      settlementLine={pricing ? formatSettlementLine(pricing) : null}
    />
  );
}

function ActivationWizard({
  prefill,
  draft,
  feeLine,
  settlementLine,
}: {
  prefill: PaymentAccountPrefill;
  draft: PaymentAccountActivationDraft | null;
  feeLine: string | null;
  settlementLine: string | null;
}) {
  const bottomInset = useFormFooterInset();
  const activateMutation = useActivatePaymentAccount();
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [createdAccount, setCreatedAccount] = useState<PaymentAccount | null>(null);
  const [blocked, setBlocked] = useState<{ title: string; message: string } | null>(null);
  const [bankQuery, setBankQuery] = useState(prefill.bank?.bankName ?? '');
  const [pickingBank, setPickingBank] = useState(false);
  const [cepNotice, setCepNotice] = useState<string | null>(null);
  const [cepLoading, setCepLoading] = useState(false);

  const defaultValues = useMemo(
    () => applyDraft(applyPrefill(ACTIVATION_DEFAULT_VALUES, prefill), draft),
    // Só na montagem: o formulário é dono do estado depois disso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { control, trigger, setValue, getValues, getFieldState, setError, handleSubmit, reset } =
    useForm<ActivationForm>({
      resolver: zodResolver(activationSchema),
      mode: 'onTouched',
      defaultValues,
    });
  const values = useWatch({ control }) as ActivationForm;
  const isCompany = values.businessType === PAYMENT_ACCOUNT_BUSINESS_TYPE.COMPANY;
  const selectedBank = values.bank?.bankCode
    ? BANKS.find((b) => b.code === values.bank.bankCode)
    : undefined;

  // Rascunho local: só endereço e banco (PLANO, seção 2.5) — CPF, nascimento
  // e PEP nunca chegam a este efeito.
  const draftDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (draftDebounce.current) clearTimeout(draftDebounce.current);
    draftDebounce.current = setTimeout(() => {
      void savePaymentAccountDraft(toDraft(values));
    }, 500);
    return () => {
      if (draftDebounce.current) clearTimeout(draftDebounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.businessType, values.address, values.bank]);

  const firstError = (fields: string[]) =>
    fields.map((f) => getFieldState(f as FieldPath<ActivationForm>).error?.message).find(Boolean);

  const goTo = async (target: number) => {
    if (target <= step) {
      setStep(target);
      return;
    }
    for (let s = step; s < target; s++) {
      const ok = await trigger(ACTIVATION_STEP_FIELDS[s] as FieldPath<ActivationForm>[]);
      if (!ok) {
        setStep(s);
        showToast(
          firstError(ACTIVATION_STEP_FIELDS[s]) ?? 'Complete os campos destacados.',
          'warning',
        );
        return;
      }
    }
    setStep(target);
    setReached((r) => Math.max(r, target));
  };

  // Só em dev: preenche as cinco etapas com os dados de teste e para na
  // revisão — falta só tocar em concluir.
  const fillWithTestData = () => {
    reset(DEV_ACTIVATION_TEST_VALUES);
    setBankQuery(DEV_ACTIVATION_TEST_VALUES.bank.bankName);
    setPickingBank(false);
    setCepNotice(null);
    setReached(4);
    setStep(4);
  };

  const handleCepBlur = async () => {
    const digits = (getValues('address.zipCode') ?? '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    setCepNotice(null);
    try {
      const found = await fetchAddressByCep(digits);
      setValue('address.street', found.street, { shouldValidate: true });
      setValue('address.neighborhood', found.neighborhood, { shouldValidate: true });
      setValue('address.city', found.city, { shouldValidate: true });
      setValue('address.state', found.state, { shouldValidate: true });
    } catch {
      setCepNotice(ACTIVATION_STEP_ADDRESS.zipCodeNotFound);
    } finally {
      setCepLoading(false);
    }
  };

  const selectBank = (bank: Bank) => {
    setValue('bank.bankCode', bank.code, { shouldValidate: true });
    setValue('bank.bankName', bank.name, { shouldValidate: true });
    setPickingBank(false);
  };

  const onSubmit = handleSubmit(async (form) => {
    // Regra de runtime (calm-ui, seção 5): reforço além do schema — sem
    // banco escolhido, não faz sentido chamar o servidor.
    if (!form.bank.bankCode) {
      setStep(3);
      showToast(ACTIVATION_STEP_BANK.bankLabel + ': escolha o banco.', 'warning');
      return;
    }
    const payload = toActivationPayload(form);
    try {
      const account = await activateMutation.mutateAsync(payload);
      await clearPaymentAccountDraft();
      setCreatedAccount(account);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const code = getApiErrorCode(err);

      if (status === 403) {
        setBlocked({
          title: ACTIVATION_BLOCKED_FALLBACK.title,
          message: getApiErrorMessage(err, ACTIVATION_BLOCKED_FALLBACK.body),
        });
        return;
      }
      if (code === 'PAYMENT_ACCOUNT_PLAN_INELIGIBLE') {
        setBlocked({
          title: ACTIVATION_PLAN_REQUIRED_FALLBACK.title,
          message: getApiErrorMessage(err, ACTIVATION_PLAN_REQUIRED_FALLBACK.body),
        });
        return;
      }

      const rawField = (err as { response?: { data?: { field?: unknown } } })?.response?.data
        ?.field;
      const field = mapServerFieldToFormPath(typeof rawField === 'string' ? rawField : undefined);
      const message = getApiErrorMessage(err, 'Não foi possível concluir agora. Tente de novo.');
      if (field) {
        setError(field as FieldPath<ActivationForm>, { type: 'server', message });
        setStep(stepForField(field));
      }
      showToast(message, field ? 'warning' : 'error');
    }
  });

  if (createdAccount) return <PaymentAccountStatusScreen account={createdAccount} />;

  if (blocked) {
    return (
      <PageLayout title="Ativar recebimento automático" onBack={goBack}>
        <ErrorState title={blocked.title} message={blocked.message} />
      </PageLayout>
    );
  }

  const isReview = step === 4;
  const footer = (
    <View
      className="flex-row gap-3 border-t border-neutral-900/5 bg-card px-5 pt-3"
      style={{ paddingBottom: 8 + bottomInset }}
    >
      {step > 0 ? (
        <PressableScale
          haptic
          onPress={() => void goTo(step - 1)}
          className="h-12 w-24 items-center justify-center rounded-full border border-neutral-200 bg-card"
        >
          <ThemedText className="font-bold text-sm text-neutral-800">Voltar</ThemedText>
        </PressableScale>
      ) : null}
      <View className="flex-1">
        <LoadingButton
          title={isReview ? ACTIVATION_STEP_REVIEW.continueButton : 'Continuar'}
          size="lg"
          loading={isReview && activateMutation.isPending}
          disabled={isReview && activateMutation.isPending}
          onPress={() => (isReview ? void onSubmit() : void goTo(step + 1))}
        />
      </View>
    </View>
  );

  return (
    <PageLayout title="Ativar recebimento automático" onBack={goBack} footer={footer}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8 }}
      >
        {STEP_LABELS.map((label, i) => (
          <Pressable
            key={label}
            onPress={() => void goTo(i)}
            className={cn(
              'h-9 flex-row items-center gap-2 rounded-full border px-3',
              i === step ? 'border-transparent bg-primary/10' : 'border-neutral-200 bg-card',
            )}
          >
            <StepDot index={i} step={step} reached={reached} />
            <ThemedText
              className={cn(
                'text-xs',
                i === step ? 'font-semibold text-neutral-900' : 'text-neutral-500',
              )}
            >
              {label}
            </ThemedText>
          </Pressable>
        ))}
      </ScrollView>

      {TEST_TOOLS_ENABLED ? (
        <Pressable
          onPress={fillWithTestData}
          className="h-9 flex-row items-center gap-2 self-start rounded-full border border-dashed border-neutral-300 px-3"
          testID="activation-dev-fill"
        >
          <Ionicons className="text-neutral-500" name="flask-outline" size={14} />
          <ThemedText className="text-xs text-neutral-600">Preencher com dados de teste</ThemedText>
        </Pressable>
      ) : null}

      {/* 1 · Você */}
      <View style={{ display: step === 0 ? 'flex' : 'none' }} className="gap-4">
        <StepCard>
          <TextFieldRow
            control={control}
            name="legalName"
            title={ACTIVATION_STEP_YOU.legalNameLabel}
            note={ACTIVATION_STEP_YOU.legalNameHelper}
            placeholder="Nome completo"
          />
          <MaskedFieldRow
            control={control}
            name="document"
            title={ACTIVATION_STEP_YOU.documentLabel}
            mask={Masks.BRL_CPF}
            placeholder="000.000.000-00"
          />
          <MaskedFieldRow
            control={control}
            name="birthDate"
            title={ACTIVATION_STEP_YOU.birthDateLabel}
            mask={Masks.DATE_DDMMYYYY}
            placeholder="DD/MM/AAAA"
          />
          <View className="flex-row items-center gap-3 py-1.5">
            <View className="flex-1 gap-0.5">
              <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
                {ACTIVATION_STEP_YOU.companyLinkLabel}
              </ThemedText>
            </View>
            <Switch
              checked={isCompany}
              onCheckedChange={(checked) => {
                setValue(
                  'businessType',
                  checked
                    ? PAYMENT_ACCOUNT_BUSINESS_TYPE.COMPANY
                    : PAYMENT_ACCOUNT_BUSINESS_TYPE.INDIVIDUAL,
                  { shouldValidate: true },
                );
                if (!checked) setValue('companyDocument', '', { shouldValidate: true });
              }}
              testID="activation-company-switch"
            />
          </View>
          {isCompany ? (
            <MaskedFieldRow
              control={control}
              name="companyDocument"
              title={ACTIVATION_STEP_YOU.companyDocumentLabel}
              mask={Masks.BRL_CNPJ}
              placeholder="00.000.000/0000-00"
            />
          ) : null}
        </StepCard>
      </View>

      {/* 2 · Endereço */}
      <View style={{ display: step === 1 ? 'flex' : 'none' }} className="gap-4">
        <StepCard>
          <MaskedFieldRow
            control={control}
            name="address.zipCode"
            title={ACTIVATION_STEP_ADDRESS.zipCodeLabel}
            note={cepLoading ? 'Buscando…' : (cepNotice ?? ACTIVATION_STEP_ADDRESS.zipCodeHelper)}
            mask={CEP_MASK}
            placeholder="00000-000"
            onExtraBlur={() => void handleCepBlur()}
          />
          <TextFieldRow
            control={control}
            name="address.street"
            title={ACTIVATION_STEP_ADDRESS.streetLabel}
          />
          <View className="flex-row gap-3">
            <View className="flex-1">
              <TextFieldRow
                control={control}
                name="address.number"
                title={ACTIVATION_STEP_ADDRESS.numberLabel}
                keyboardType="number-pad"
              />
            </View>
            <View className="flex-[1.4]">
              <TextFieldRow
                control={control}
                name="address.complement"
                title={ACTIVATION_STEP_ADDRESS.complementLabel}
              />
            </View>
          </View>
          <TextFieldRow
            control={control}
            name="address.neighborhood"
            title={ACTIVATION_STEP_ADDRESS.neighborhoodLabel}
          />
          <View className="flex-row gap-3">
            <View className="flex-[2]">
              <TextFieldRow
                control={control}
                name="address.city"
                title={ACTIVATION_STEP_ADDRESS.cityLabel}
              />
            </View>
            <View className="flex-1">
              <TextFieldRow
                control={control}
                name="address.state"
                title={ACTIVATION_STEP_ADDRESS.stateLabel}
                maxLength={2}
                autoCapitalize="characters"
              />
            </View>
          </View>
        </StepCard>
      </View>

      {/* 3 · PEP */}
      <View style={{ display: step === 2 ? 'flex' : 'none' }} className="gap-4">
        <StepCard>
          <ControlledOptionRows
            control={control}
            name="isPoliticallyExposed"
            title={ACTIVATION_STEP_PEP.question}
            options={[
              { value: true, label: ACTIVATION_STEP_PEP.yesLabel },
              { value: false, label: ACTIVATION_STEP_PEP.noLabel },
            ]}
            testID="activation-pep"
          />
          <ThemedText className="text-xs leading-4 text-neutral-500">
            {ACTIVATION_STEP_PEP.helper}
          </ThemedText>
        </StepCard>
      </View>

      {/* 4 · Banco */}
      <View style={{ display: step === 3 ? 'flex' : 'none' }} className="gap-4">
        <StepCard>
          <FieldBlock
            title={ACTIVATION_STEP_BANK.bankLabel}
            error={getFieldState('bank.bankCode').error?.message}
          >
            {selectedBank && !pickingBank ? (
              <Pressable
                onPress={() => setPickingBank(true)}
                className="h-11 flex-row items-center justify-between rounded-xl border border-neutral-200 bg-card px-3.5"
                style={{ borderCurve: 'continuous' }}
                testID="activation-bank-selected"
              >
                <ThemedText className="text-sm text-neutral-900">
                  {selectedBank.code} · {selectedBank.name}
                </ThemedText>
                <Ionicons className="text-neutral-500" name="chevron-down" size={16} />
              </Pressable>
            ) : (
              <View className="gap-2">
                <Input
                  value={bankQuery}
                  onChangeText={setBankQuery}
                  placeholder={ACTIVATION_STEP_BANK.bankSearchPlaceholder}
                  className="h-11 text-sm"
                  testID="activation-bank-search"
                />
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  style={{ maxHeight: 220 }}
                  className="overflow-hidden rounded-xl border border-neutral-900/5"
                >
                  {searchBanks(bankQuery).map((bank, index) => (
                    <PressableScale
                      key={bank.code}
                      haptic
                      onPress={() => selectBank(bank)}
                      className={cn(
                        'px-3.5 py-3',
                        index > 0 ? 'border-t border-neutral-100' : '',
                        'bg-card',
                      )}
                      testID={`activation-bank-option-${bank.code}`}
                    >
                      <ThemedText className="text-sm text-neutral-800">
                        {bank.code} · {bank.name}
                      </ThemedText>
                    </PressableScale>
                  ))}
                </ScrollView>
              </View>
            )}
          </FieldBlock>

          <View className="flex-row gap-3">
            <View className="flex-1">
              <MaskedFieldRow
                control={control}
                name="bank.agency"
                title={ACTIVATION_STEP_BANK.agencyLabel}
                mask={AGENCY_MASK}
                placeholder="0000"
              />
            </View>
            <View className="flex-[1.4]">
              <MaskedFieldRow
                control={control}
                name="bank.account"
                title={ACTIVATION_STEP_BANK.accountLabel}
                mask={ACCOUNT_MASK}
                placeholder="00000000"
              />
            </View>
            <View style={{ width: 72 }}>
              <MaskedFieldRow
                control={control}
                name="bank.accountDigit"
                title={ACTIVATION_STEP_BANK.accountDigitLabel}
                mask={ACCOUNT_DIGIT_MASK}
                placeholder="0"
              />
            </View>
          </View>

          <ControlledOptionRows
            control={control}
            name="bank.accountType"
            title={ACTIVATION_STEP_BANK.accountTypeLabel}
            options={[
              {
                value: PAYMENT_ACCOUNT_ACCOUNT_TYPE.CHECKING,
                label: ACTIVATION_STEP_BANK.accountTypeCheckingLabel,
              },
              {
                value: PAYMENT_ACCOUNT_ACCOUNT_TYPE.SAVINGS,
                label: ACTIVATION_STEP_BANK.accountTypeSavingsLabel,
              },
            ]}
            testID="activation-account-type"
          />
          <ThemedText className="text-xs leading-4 text-neutral-500">
            {ACTIVATION_STEP_BANK.notice}
          </ThemedText>
        </StepCard>
      </View>

      {/* 5 · Revisão */}
      <View style={{ display: step === 4 ? 'flex' : 'none' }} className="gap-4">
        <StepCard>
          <ThemedText className="font-semibold text-[15px] text-neutral-900">
            {ACTIVATION_STEP_REVIEW.summaryTitle}
          </ThemedText>
          <SummaryRow label="Nome" value={values.legalName} />
          <SummaryRow
            label="Documento"
            value={isCompany ? (values.companyDocument ?? '') : values.document}
          />
          <SummaryRow label="Nascimento" value={values.birthDate} />
          <SummaryRow
            label="Endereço"
            value={[values.address?.street, values.address?.number, values.address?.city]
              .filter(Boolean)
              .join(', ')}
          />
          <SummaryRow
            label="Banco"
            value={
              selectedBank
                ? `${selectedBank.name} · ag. ${values.bank?.agency} · cc ${values.bank?.account}${values.bank?.accountDigit ? `-${values.bank.accountDigit}` : ''}`
                : ''
            }
          />
          {feeLine ? <SummaryRow label="Taxas" value={feeLine} /> : null}
          {settlementLine ? <SummaryRow label="Prazos" value={settlementLine} /> : null}
        </StepCard>

        <Controller
          control={control}
          name="acceptedTerms"
          render={({ field, fieldState }) => (
            <View className="gap-1.5">
              <Pressable
                onPress={() => field.onChange(!field.value)}
                className="flex-row items-start gap-3 py-1"
                testID="activation-accept-terms"
              >
                <View className="pt-0.5">
                  <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                </View>
                <ThemedText className="flex-1 text-sm leading-5 text-neutral-700">
                  {termsBefore}
                  <ThemedText
                    onPress={() => router.push('/terms' as never)}
                    className="font-semibold text-sm text-primary underline"
                  >
                    {ACTIVATION_STEP_REVIEW.termsLinkLabel}
                  </ThemedText>
                  {termsAfter}
                </ThemedText>
              </Pressable>
              {fieldState.error ? (
                <ThemedText className="text-xs text-error-600">
                  {fieldState.error.message}
                </ThemedText>
              ) : (
                <ThemedText className="text-[11px] text-neutral-400">
                  Versão {PAYMENTS_TERMS_VERSION}
                </ThemedText>
              )}
            </View>
          )}
        />
      </View>
    </PageLayout>
  );
}

function StepDot({ index, step, reached }: { index: number; step: number; reached: number }) {
  const done = index !== step && (index < step || index < reached);
  return (
    <View
      className={cn(
        'h-6 w-6 items-center justify-center rounded-full',
        index === step ? 'bg-primary' : done ? 'bg-success-50' : 'bg-neutral-100',
      )}
    >
      {done && index !== step ? (
        <Ionicons name="checkmark" size={13} color="#137A63" />
      ) : (
        <ThemedText
          className={cn(
            'font-bold text-[11px]',
            index === step ? 'text-white' : 'text-neutral-500',
          )}
        >
          {index + 1}
        </ThemedText>
      )}
    </View>
  );
}

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <View className="flex-row items-start justify-between gap-3 border-t border-neutral-100 py-2 first:border-t-0">
      <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
      <ThemedText className="flex-1 text-right font-medium text-sm text-neutral-900">
        {value}
      </ThemedText>
    </View>
  );
}

function TextFieldRow({
  control,
  name,
  title,
  note,
  placeholder,
  keyboardType,
  maxLength,
  autoCapitalize,
}: {
  control: Control<ActivationForm>;
  name: FieldPath<ActivationForm>;
  title: string;
  note?: string;
  placeholder?: string;
  keyboardType?: 'default' | 'number-pad';
  maxLength?: number;
  autoCapitalize?: 'none' | 'characters';
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldBlock title={title} note={note} error={fieldState.error?.message}>
          <Input
            value={typeof field.value === 'string' ? field.value : ''}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            keyboardType={keyboardType}
            maxLength={maxLength}
            autoCapitalize={autoCapitalize}
            className="h-11 text-sm"
          />
        </FieldBlock>
      )}
    />
  );
}

function MaskedFieldRow({
  control,
  name,
  title,
  note,
  placeholder,
  mask,
  onExtraBlur,
}: {
  control: Control<ActivationForm>;
  name: FieldPath<ActivationForm>;
  title: string;
  note?: string;
  placeholder?: string;
  mask: Mask;
  onExtraBlur?: () => void;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldBlock title={title} note={note} error={fieldState.error?.message}>
          <MaskInput
            value={typeof field.value === 'string' ? field.value : ''}
            onChangeText={(masked) => field.onChange(masked)}
            onBlur={() => {
              field.onBlur();
              onExtraBlur?.();
            }}
            mask={mask}
            keyboardType="number-pad"
            placeholder={placeholder}
            placeholderTextColor="#A3A3A3"
            style={webInputReset}
            className="h-11 rounded-xl border border-neutral-200 bg-card px-3.5 text-sm text-neutral-900"
          />
        </FieldBlock>
      )}
    />
  );
}
